import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import { useI18n } from '../../i18n'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import chartStyles from '../../styles/pages/home/Home.module.scss'

const COLORS = ['#56a3d9', '#10b981', '#f59e0b', '#ef4444', '#3b82f6']

/** Matches .postureSubtitle on .onDarkSurface */
const CHART_AXIS_COLOR = 'rgba(255, 255, 255, 0.68)'
const CHART_GRID_COLOR = 'rgba(255, 255, 255, 0.18)'
const CHART_AXIS_LINE = 'rgba(255, 255, 255, 0.28)'

type TrendRow = Record<string, string | number>

type FrameworkSeries = {
  framework?: string
  data?: Array<{ date?: string; value?: number }>
}

type Props = {
  trendData: TrendRow[]
  complianceTrend: Array<{ date: string; value: number }>
  frameworkSeries: FrameworkSeries[]
}

export default function HomeComplianceTrendPanel({
  trendData,
  complianceTrend,
  frameworkSeries,
}: Props) {
  const { t } = useI18n()
  const hasHistory = trendData.length > 0 || complianceTrend.length > 0
  const chartRows = trendData.length > 0 ? trendData : complianceTrend
  const multiSeries = trendData.length > 0 && frameworkSeries.length > 0

  return (
    <section
      className={`${entStyles.layer} ${entStyles.onDarkSurface}`}
      data-testid="home-compliance-trend"
    >
      <p className={entStyles.layerLabel}>
        {t('home.complianceTrend.layerLabel', 'Compliance & process trend')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>
          {t('titles.complianceTrend', 'Compliance Trend (30 days)')}
        </h2>
        <p className={entStyles.postureSubtitle}>
          {t(
            'home.complianceTrend.hint',
            'Self-assessed and verified compliance by standard. When historical metrics are unavailable, the latest snapshot is shown.',
          )}
        </p>
        <div
          className={`chart-wrapper ${chartStyles.chartWrapper} ${entStyles.postureChart}`}
        >
          {!hasHistory ? (
            <div className={`${chartStyles.chartEmpty} ${chartStyles.chartEmptyOnDark}`}>
              <div className={chartStyles.chartEmptyInner}>
                <div className={chartStyles.chartEmptyLine}>
                  {t('home.complianceTrend.empty', 'No trend data yet')}
                </div>
                <div className={chartStyles.chartEmptyHint}>
                  {t(
                    'home.complianceTrend.emptyHint',
                    'Complete assessments or enable standards in Starter Guide to populate this chart.',
                  )}
                </div>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: CHART_AXIS_COLOR }}
                  stroke={CHART_AXIS_LINE}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 12, fill: CHART_AXIS_COLOR }}
                  stroke={CHART_AXIS_LINE}
                />
                <Tooltip
                  formatter={(value: number) => `${value}%`}
                  contentStyle={{
                    background: 'rgba(15, 23, 42, 0.92)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'rgba(255, 255, 255, 0.95)',
                  }}
                  labelStyle={{ color: CHART_AXIS_COLOR }}
                  itemStyle={{ color: 'rgba(255, 255, 255, 0.9)' }}
                />
                <Legend wrapperStyle={{ color: CHART_AXIS_COLOR }} />
                {multiSeries ? (
                  frameworkSeries.map((f, idx) => (
                    <Line
                      key={f.framework || idx}
                      type="monotone"
                      dataKey={String(f.framework)}
                      stroke={COLORS[idx % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name={String(f.framework)}
                    />
                  ))
                ) : (
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name={t('home.complianceTrend.complianceLine', 'Compliance')}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </section>
  )
}
